import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Linkedin, ArrowLeft, X, Mail, Menu } from 'lucide-react';

const speakingTopics = [
  {
    title: 'Luxury & Sustainable Travel',
    description: 'The future of travel lies in thoughtful luxury, cultural depth and responsible economic participation. Naledi explores how travel businesses can create high-value experiences that are commercially viable, community-aware and globally appealing.',
    audience: 'Tourism boards, hospitality brands, travel industry, investors',
    formats: 'Keynote, Panel, Private Roundtable',
  },
  {
    title: 'Supply Chain & Trade',
    description: 'Building stronger pathways between Southern Africa and global markets requires more than sourcing alone. This topic explores ethical trade, supplier readiness, trust, infrastructure and how to create meaningful market access for emerging producers. Through our own Spaza Marketplace infrastructure, we are exploring real-life trade corridor solutions — including our MAPS pilot.',
    audience: 'Procurement teams, trade bodies, import/export businesses, government',
    formats: 'Keynote, Workshop, Advisory Session',
  },
  {
    title: 'Scaling Globally',
    description: 'Lessons from building across the Southern Africa–UK corridor and beyond. Naledi shares practical insight on international positioning, market entry, cross-border operations, partnership strategy and navigating growth across different business environments.',
    audience: 'Scale-ups, investors, trade delegations, business schools',
    formats: 'Keynote, Masterclass, Panel',
  },
  {
    title: 'Leadership & Legacy',
    description: 'How intentional leadership, cultural intelligence and long-term thinking shape businesses and ecosystems that endure. This session reflects on leading with clarity, conviction and purpose while building work that leaves a meaningful imprint.',
    audience: 'Senior leaders, boards, NGOs, women in leadership programmes',
    formats: 'Keynote, Workshop, Fireside Chat',
  },
  {
    title: 'Tech & Innovation',
    description: 'Technology has the power to connect communities, unlock access and reshape traditional industries when built with purpose. Naledi explores platform thinking, inclusive innovation and how tech can support real economic participation across sectors.',
    audience: 'Tech companies, investors, innovation hubs, corporates',
    formats: 'Keynote, Panel, Advisory Session',
  },
];

const AdvisoryPage = () => {
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', system-ui, sans-serif" }} data-testid="advisory-page">
      <nav className="w-full bg-white/95 backdrop-blur-md" style={{ borderBottom: '1px solid #E8E0D4' }}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 no-underline">
            <img src="/maluti-icon.png" alt="Maluti Holdings" className="w-7 h-7 object-contain" />
            <span className="text-sm font-semibold tracking-[0.2em] uppercase" style={{ color: '#2C1A0E' }}>Maluti</span>
            <span className="text-sm font-light tracking-[0.2em] uppercase" style={{ color: '#B8860B' }}>Holdings</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <a href="/#portfolio" className="text-xs uppercase tracking-[0.15em] transition-colors duration-300 hover:text-[#B8860B] no-underline" style={{ color: '#8B7355' }}>Portfolio</a>
            <Link to="/vision" className="text-xs uppercase tracking-[0.15em] transition-colors duration-300 hover:text-[#B8860B] no-underline" style={{ color: '#8B7355' }}>Vision</Link>
            <span className="text-xs uppercase tracking-[0.15em] font-semibold" style={{ color: '#2C1A0E' }}>Speaking</span>
            <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 px-4 py-2 text-[10px] uppercase tracking-[0.15em] font-medium transition-all duration-300 hover:opacity-90" style={{ backgroundColor: '#8B7355', color: '#F5F0E8' }}>Ops Suite</button>
          </div>
          <button className="md:hidden p-1" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-btn">
            {menuOpen ? <X className="w-6 h-6" style={{ color: '#2C1A0E' }} /> : <Menu className="w-6 h-6" style={{ color: '#2C1A0E' }} />}
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden px-6 pb-4 bg-white flex flex-col gap-3" style={{ borderTop: '1px solid #E8E0D4' }}>
            <a href="/#portfolio" onClick={() => setMenuOpen(false)} className="text-xs uppercase tracking-[0.15em] py-2 transition-colors hover:text-[#B8860B] no-underline" style={{ color: '#8B7355' }}>Portfolio</a>
            <Link to="/vision" onClick={() => setMenuOpen(false)} className="text-xs uppercase tracking-[0.15em] py-2 transition-colors hover:text-[#B8860B] no-underline" style={{ color: '#8B7355' }}>Vision</Link>
            <span className="text-xs uppercase tracking-[0.15em] py-2 font-semibold" style={{ color: '#2C1A0E' }}>Speaking & Advisory</span>
            <button onClick={() => { navigate('/dashboard'); setMenuOpen(false); }} className="flex items-center gap-2 px-4 py-2 text-[10px] uppercase tracking-[0.15em] font-medium self-start" style={{ backgroundColor: '#8B7355', color: '#F5F0E8' }}>Ops Suite <ArrowRight className="w-3 h-3" /></button>
          </div>
        )}
      </nav>

      {/* Banner */}
      <div className="relative">
        <div className="h-52 sm:h-56 w-full overflow-hidden relative">
          <img src="https://images.unsplash.com/photo-1758883019110-04c79dc56a71?w=1400&q=80" alt="Cultural leadership" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(44,26,14,0.25) 0%, rgba(44,26,14,0.6) 100%)' }} />
          <div className="absolute inset-0 flex flex-col items-center justify-center px-6 gap-2">
            <img src="/naledi-photo.jpg" alt="Naledi McCarthy-Ocansey" className="w-28 h-28 rounded-full object-cover shadow-xl" style={{ border: '4px solid rgba(255,255,255,0.2)', outline: '3px solid #D4AF37' }} />
            <p className="text-xs sm:text-sm uppercase tracking-[0.3em] font-medium text-center" style={{ color: '#D4AF37' }}>Cultural Leader &middot; Brand Architect &middot; Global Strategist</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <section className="pt-8 pb-16 sm:pb-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl sm:text-4xl font-light mb-2 tracking-tight" style={{ color: '#2C1A0E' }}>Engagements & Collaboration</h1>
          <p className="text-sm mb-0.5 font-medium" style={{ color: '#B8860B' }}>Naledi McCarthy-Ocansey, MSc Int'l Business, PG Dip</p>
          <p className="text-xs mb-3" style={{ color: '#6B5B4E' }}>Cultural Branding & Business Architect</p>
          <a href="https://www.linkedin.com/in/naledimccarthy/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-xs font-medium mb-8 transition-opacity hover:opacity-70 no-underline" style={{ color: '#0A66C2' }} data-testid="advisory-linkedin-link">
            <Linkedin className="w-4 h-4" /> Connect on LinkedIn
          </a>

          {/* Bio */}
          <div className="text-left max-w-3xl mx-auto mb-12 space-y-4">
            <p className="text-sm leading-[1.85]" style={{ color: '#6B5B4E' }}>
              Naledi McCarthy-Ocansey is a founder, advisor and cultural trade strategist working at the intersection of culture, commerce, market access, luxury travel, cross-border business and innovation. Her work spans branding, sourcing, trade development, tourism, creative enterprise and platform strategy, with a particular focus on translating Southern African value, identity and enterprise into globally relevant opportunities.
            </p>
            <p className="text-sm leading-[1.85]" style={{ color: '#6B5B4E' }}>
              As the founder of ventures across cultural commerce, media, talent, trade infrastructure and experience-led business, she has built and advised on projects that connect products, people and ideas to stronger positioning, wider markets and long-term commercial potential. She is known for combining strategic depth with cultural intelligence, helping brands and organisations move beyond inspiration into structured, credible execution.
            </p>
            <p className="text-sm leading-[1.85]" style={{ color: '#6B5B4E' }}>
              Drawing from a background in business administration, marketing, project risk management and international business management, Naledi brings both founder realism and advisory strength to every engagement. Whether speaking on branding, entrepreneurship, travel, trade, technology or leadership, she offers insight grounded in lived execution across industries, markets and evolving business models.
            </p>
            <p className="text-sm leading-[1.85] font-medium" style={{ color: '#2C1A0E' }}>
              Available for keynotes, panels, workshops, private events and strategic advisory engagements. Collaborating with industry leaders, founders, startups and organisations working at the intersection of culture, commerce, luxury travel, trade and technology.
            </p>
          </div>

          {/* Topic Cards — 3x2 balanced grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8 text-left">
            {speakingTopics.map(topic => (
              <button
                key={topic.title}
                onClick={() => setSelectedTopic(topic)}
                className="p-5 border text-left transition-all duration-300 hover:-translate-y-1 hover:shadow-md group cursor-pointer"
                style={{ borderColor: '#E8E0D4', backgroundColor: '#FAF7F2' }}
                data-testid={`topic-card-${topic.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <h3 className="text-sm font-semibold mb-2" style={{ color: '#2C1A0E' }}>{topic.title}</h3>
                <p className="text-xs leading-relaxed line-clamp-2 mb-2" style={{ color: '#6B5B4E' }}>{topic.description}</p>
                <p className="text-[10px] mb-3" style={{ color: '#8B7355' }}>Ideal for: {topic.audience}</p>
                <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: '#B8860B' }}>Learn more &rarr;</p>
              </button>
            ))}
            {/* 6th card — Industry Engagements with logo-style badges */}
            <div className="p-5 border text-left" style={{ borderColor: '#E8E0D4', backgroundColor: '#FAF7F2' }} data-testid="clients-card">
              <h3 className="text-sm font-semibold mb-3" style={{ color: '#2C1A0E' }}>Industry Engagements</h3>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { name: 'SA Tourism Board', initials: 'SAT', bg: '#006B3F', color: '#FFF' },
                  { name: 'BBC 1Xtra', initials: 'BBC', bg: '#000', color: '#FFF' },
                  { name: 'Chelsea Stadium', initials: 'CFC', bg: '#034694', color: '#FFF' },
                  { name: 'ATTA', initials: 'ATTA', bg: '#B8860B', color: '#FFF' },
                  { name: 'Olympia London', initials: 'OL', bg: '#2C1A0E', color: '#D4AF37' },
                  { name: 'Digital Mobility Fund', initials: 'DMF', bg: '#BA0C2F', color: '#FFF' },
                  { name: 'SAMPRA', initials: 'SA', bg: '#1A365D', color: '#FFF' },
                  { name: 'Shooting Star Chase', initials: 'SS', bg: '#7B2D8B', color: '#FFF' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-[7px] font-bold" style={{ backgroundColor: item.bg, color: item.color }}>
                      {item.initials}
                    </div>
                    <p className="text-[9px] leading-tight" style={{ color: '#6B5B4E' }}>{item.name}</p>
                  </div>
                ))}
              </div>
              <Link to="/collaborations" className="text-[10px] mt-2 font-medium no-underline inline-block hover:underline" style={{ color: '#B8860B' }}>See more &rarr;</Link>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="mailto:hello@malutiholdings.com?subject=Speaking%20Enquiry%20-%20General" className="inline-flex items-center gap-3 px-10 py-4 text-xs uppercase tracking-[0.2em] font-medium transition-all duration-300 hover:opacity-90 no-underline" style={{ backgroundColor: '#2C1A0E', color: '#F5F0E8' }} data-testid="book-speaking-btn">
              General Enquiry <ArrowRight className="w-4 h-4" />
            </a>
            <a href="https://www.linkedin.com/in/naledimccarthy/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-4 text-xs uppercase tracking-[0.15em] font-medium border transition-all duration-300 hover:bg-[#0A66C2] hover:text-white hover:border-[#0A66C2] no-underline" style={{ borderColor: '#0A66C2', color: '#0A66C2' }}>
              <Linkedin className="w-4 h-4" /> LinkedIn Profile
            </a>
          </div>
        </div>
      </section>

      {/* Topic Detail Modal */}
      {selectedTopic && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(44,26,14,0.6)' }} onClick={() => setSelectedTopic(null)}>
          <div className="bg-white max-w-lg w-full p-8 relative shadow-2xl" onClick={e => e.stopPropagation()} data-testid="topic-modal">
            <button onClick={() => setSelectedTopic(null)} className="absolute top-4 right-4 transition-opacity hover:opacity-60" style={{ color: '#8B7355' }}>
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-px" style={{ backgroundColor: '#B8860B' }} />
              <span className="text-[10px] uppercase tracking-[0.3em] font-semibold" style={{ color: '#B8860B' }}>Engagement Topic</span>
            </div>
            <h2 className="text-2xl font-light mb-4 tracking-tight" style={{ color: '#2C1A0E' }}>{selectedTopic.title}</h2>
            <p className="text-sm leading-[1.8] mb-6" style={{ color: '#6B5B4E' }}>{selectedTopic.description}</p>

            <div className="flex flex-col gap-3 mb-6 p-4" style={{ backgroundColor: '#FAF7F2', border: '1px solid #E8E0D4' }}>
              <div>
                <p className="text-[10px] uppercase tracking-wider font-semibold mb-0.5" style={{ color: '#8B7355' }}>Ideal For</p>
                <p className="text-xs" style={{ color: '#6B5B4E' }}>{selectedTopic.audience}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider font-semibold mb-0.5" style={{ color: '#8B7355' }}>Available Formats</p>
                <p className="text-xs" style={{ color: '#6B5B4E' }}>{selectedTopic.formats}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider font-semibold mb-0.5" style={{ color: '#8B7355' }}>Investment</p>
                <p className="text-sm font-semibold" style={{ color: '#2C1A0E' }}>POA — Price on Enquiry</p>
              </div>
            </div>

            <a
              href={`mailto:hello@malutiholdings.com?subject=Speaking%20Enquiry%20-%20${encodeURIComponent(selectedTopic.title)}&body=Hi%20Naledi%2C%0A%0AI'm%20interested%20in%20your%20${encodeURIComponent(selectedTopic.title)}%20speaking%20topic.%0A%0APlease%20could%20you%20share%20more%20details%3F%0A%0AThank%20you`}
              className="inline-flex items-center gap-2 px-8 py-3 text-xs uppercase tracking-[0.2em] font-medium transition-all duration-300 hover:opacity-90 no-underline w-full justify-center"
              style={{ backgroundColor: '#2C1A0E', color: '#F5F0E8' }}
              data-testid="topic-enquiry-btn"
            >
              <Mail className="w-4 h-4" /> Enquire about {selectedTopic.title}
            </a>
          </div>
        </div>
      )}

      <footer className="py-6 px-6 text-center" style={{ backgroundColor: '#2C1A0E' }}>
        <p className="text-xs" style={{ color: '#8B7355' }}>&copy; {new Date().getFullYear()} Maluti Holdings &middot; hello@malutiholdings.com</p>
      </footer>
    </div>
  );
};

export default AdvisoryPage;
